import { fetchPortfolioAbilities } from "portfolio/api/teacher";
import { PORTFOLIO_ABILITIES } from "portfolio/constants";
import type { PortfolioAbilities } from "portfolio/types";
import { computed, ref } from "vue";

const abilities = ref<PortfolioAbilities>({
  [PORTFOLIO_ABILITIES.CAN_CREATE_TASK]: false,
  [PORTFOLIO_ABILITIES.CAN_CREATE_PUBLIC_TASK]: false,
  [PORTFOLIO_ABILITIES.CAN_CREATE_TEMPLATE]: false,
});

let fetchPromise: Promise<PortfolioAbilities> | null = null;
let hasFetched = false;

const loadAbilities = async (): Promise<PortfolioAbilities> => {
  if (fetchPromise) {
    return fetchPromise;
  }

  if (hasFetched) {
    return abilities.value;
  }

  fetchPromise = (async () => {
    try {
      abilities.value = await fetchPortfolioAbilities();
      hasFetched = true;
      return abilities.value;
    } finally {
      fetchPromise = null;
    }
  })();

  return fetchPromise;
};

export const usePortfolioAbilities = () => {
  // Auto-load on first use — deduplication prevents multiple fetches
  loadAbilities();

  const canCreateTask = computed(
    () => abilities.value[PORTFOLIO_ABILITIES.CAN_CREATE_TASK],
  );

  const canCreatePublicTask = computed(
    () => abilities.value[PORTFOLIO_ABILITIES.CAN_CREATE_PUBLIC_TASK],
  );

  const canCreateTemplate = computed(
    () => abilities.value[PORTFOLIO_ABILITIES.CAN_CREATE_TEMPLATE],
  );

  return {
    canCreateTask,
    canCreatePublicTask,
    canCreateTemplate,
    loadAbilities,
  };
};
